import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,Paper,
  Grid,
} from "@mui/material";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { DataGrid } from "@mui/x-data-grid";
import { useDashboardService } from "../../services/dashboardService";
import EstimatedWorkHoursChart from "./EstimatedWorkHoursChart"; // ✅ Add this import
import CheckinHeatmap from "./CheckinHeatmap"; // ✅ Add this import
const COLORS = {
  checkIn: "#A5D6A7", // light green
  checkOut: "#EF9A9A", // light red
};

const roundToNext10 = (value) => Math.ceil(value / 5) * 5;

 // ... (imports remain same)


const CheckInStatsByHour = ({ selectedDate, selectedVertical }) => {
  const { fetchCheckInStatsByHour } = useDashboardService();
  const [rawData, setRawData] = useState([]);
  const [chartMode, setChartMode] = useState("line"); // ✅ line | heatmap | gantt

  useEffect(() => {
    if (!selectedDate) return;
    fetchCheckInStatsByHour({ asOnDate: selectedDate }).then((res) => {
      setRawData(res || []);
    });
  }, [selectedDate]);

  const filteredRaw = useMemo(() => {
    if (selectedVertical === "All") return rawData;
    return rawData.filter((d) => d.Business_Vertical_Desc === selectedVertical);
  }, [rawData, selectedVertical]);

  const chartData = useMemo(() => {
    if (!filteredRaw || filteredRaw.length === 0) return [];

    const byHour = {};
    filteredRaw.forEach((item) => {
      const hour = Number(item.HourOfDay);
      const checkIn = Number(item.CheckIns || 0);
      const checkOut = Number(item.CheckOuts || 0);
      const total = Number(item.TotalEmployees || 0);

      if (!byHour[hour]) {
        byHour[hour] = { Hour: hour, checkIn: 0, checkOut: 0, total: 0 };
      }

      byHour[hour].checkIn += checkIn;
      byHour[hour].checkOut += checkOut;
      byHour[hour].total += total;
    });

    return Object.entries(byHour).map(([hour, entry]) => {
  const percentage = (val) =>
    entry.total > 0 ? (val / entry.total) * 100 : 0;
  return {
    HourNumeric: Number(hour), // Add a numeric field
    Hour: `${Math.floor(hour)}:${hour % 1 === 0.5 ? '30' : '00'}`,
    CheckIn: Number(percentage(entry.checkIn).toFixed(2)),
    CheckOut: Number(percentage(entry.checkOut).toFixed(2)),
  };
}).sort((a, b) => a.HourNumeric - b.HourNumeric); // 👈 Sort numerically

  }, [filteredRaw]);

  const maxY = useMemo(() => {
    const max = Math.max(
      ...chartData.map((d) => Math.max(d.CheckIn, d.CheckOut))
    );
    return roundToNext10(max || 100);
  }, [chartData]);

  const heatmapRows = useMemo(() => {
    if (!rawData || rawData.length === 0) return [];

    const rows = [];
    const hourKeys = [...Array(24).keys()].map((h) => `H${h}`);
    const verticals = [...new Set(rawData.map((d) => d.Business_Vertical_Desc))];

    verticals.forEach((bv, index) => {
      const rowIn = {
        id: `in-${index}`,
        type: "Check-In",
        businessVertical: bv,
      };
      const rowOut = {
        id: `out-${index}`,
        type: "Check-Out",
        businessVertical: bv,
      };

      hourKeys.forEach((h) => {
        rowIn[h] = 0;
        rowOut[h] = 0;
      });

      const filtered = rawData.filter((d) => d.Business_Vertical_Desc === bv);
      filtered.forEach((d) => {
        const hour = Number(d.HourOfDay);
        const hKey = `H${hour}`;
        const total = Number(d.TotalEmployees || 0);
        if (total > 0) {
          rowIn[hKey] = Number(((d.CheckIns / total) * 100).toFixed(1));
          rowOut[hKey] = Number(((d.CheckOuts / total) * 100).toFixed(1));
        }
      });

      rows.push(rowIn, rowOut);
    });

    return rows;
  }, [rawData, selectedDate]);

  const heatmapCols = useMemo(() => {
    const hourCols = [...Array(24).keys()].map((h) => ({
      field: `H${h}`,
      headerName: `${h}:00`,
      width: 70,
      renderCell: (params) => {
        const value = params.value || 0;
        const baseColor =
          params.row.type === "Check-In"
            ? "rgba(165, 214, 167"
            : "rgba(239, 154, 154";
        const alpha = Math.min(value / 100, 1).toFixed(2);
        const bgColor = `${baseColor}, ${alpha})`;

        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: bgColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              color: value > 50 ? "#fff" : "#000",
            }}
          >
            {value > 0 ? `${value.toFixed(1)}%` : ""}
          </Box>
        );
      },
    }));

    return [
      { field: "type", headerName: "Type", width: 100 },
      { field: "businessVertical", headerName: "Business Vertical", width: 160 },
      ...hourCols,
    ];
  }, []);

  return (
    <Box elevation={2}   
     sx={{ 
        borderRadius: 2,
        p: 1, height: "100%",
        boxShadow: 1,
        bgcolor: "background.paper", 
        alignItems: "center", 
      }}
    
    >
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <Typography variant="h6">Hourly Check-In / Check-Out %</Typography>
        </Grid>

        {selectedVertical === "All" && (
          <Grid item>
            <FormControlLabel
              control={
                <Switch
                  checked={chartMode === "heatmap"}
                  onChange={(e) =>
                    setChartMode(e.target.checked ? "heatmap" : "line")
                  }
                />
              }
              label="Show Heatmap"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={chartMode === "gantt"}
                  onChange={(e) =>
                    setChartMode(e.target.checked ? "gantt" : "line")
                  }
                />
              }
              label="Show Gantt View"
            />
          </Grid>
        )}
      </Grid>

      {chartMode === "line" && (
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="Hour"
               interval={2} 
            />
            <YAxis
              domain={[0, maxY]}
               // skip every other label to avoid clutter
               tickFormatter={(v, index) => index % 2 === 0 ? v : ''} 
              ticks={Array.from({ length: Math.ceil(maxY / 5) + 1 }, (_, i) => i * 5)}
            />
            <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
            <Legend />
            <Area
              type="monotone"
              dataKey="CheckIn"
              stroke="#4CAF50"
              fill="rgba(76, 175, 80, 0.3)"
              name="Check-In %"
            />
            <Area
              type="monotone"
              dataKey="CheckOut"
              stroke="#F44336"
              fill="rgba(244, 67, 54, 0.3)"
              name="Check-Out %"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}

      {chartMode === "heatmap" && (
         <Box sx={{ mt: 2 }}>
          <CheckinHeatmap rawData={rawData} />
        </Box>
      )}

      {chartMode === "gantt" && (
        <Box sx={{ mt: 2 }}>
          <EstimatedWorkHoursChart rawData={rawData} />
        </Box>
      )}
    </Box>
  );
};

export default CheckInStatsByHour;


 
